import { useEffect, useState } from 'react'
import {
  Tabs, Table, Button, Space, Modal, Form, Input, Select,
  message, Tag, Popconfirm, InputNumber
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { departmentApi, positionApi } from '../services/api'

interface Department {
  id: number
  name: string
  code: string
  parent_id: number | null
  leader: string
  sort_order: number
  status: number
  positions?: Position[]
}

interface Position {
  id: number
  name: string
  code: string
  department_id: number | null
  level: number
  sort_order: number
  status: number
  department?: { id: number; name: string }
}

// ===================== 部门管理 =====================
function DepartmentTab() {
  const [list, setList] = useState<Department[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Department | null>(null)
  const [form] = Form.useForm()

  const fetchList = async () => {
    setLoading(true)
    try {
      const res: any = await departmentApi.getList()
      if (res.code === 200) setList(Array.isArray(res.data) ? res.data : res.data?.list || [])
    } catch {
      message.error('获取部门列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchList() }, [])

  const handleAdd = () => {
    setEditing(null)
    form.resetFields()
    setModalOpen(true)
  }

  const handleEdit = (record: Department) => {
    setEditing(record)
    form.setFieldsValue(record)
    setModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      const res: any = await departmentApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        fetchList()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || '删除失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      let res: any
      if (editing) {
        res = await departmentApi.update(editing.id, values)
      } else {
        res = await departmentApi.create(values)
      }
      if (res.code === 200) {
        message.success(editing ? '更新成功' : '创建成功')
        setModalOpen(false)
        fetchList()
      } else {
        message.error(res.message || '操作失败')
      }
    } catch {
      // validation error
    }
  }

  const columns = [
    { title: '部门名称', dataIndex: 'name', key: 'name' },
    { title: '编码', dataIndex: 'code', key: 'code', render: (v: string) => v || '-' },
    { title: '负责人', dataIndex: 'leader', key: 'leader', render: (v: string) => v || '-' },
    { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v: number) => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '启用' : '停用'}</Tag>
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, r: Department) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该部门？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          style={{ backgroundColor: '#55266c' }}
        >新增部门</Button>
      </div>
      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />
      <Modal
        title={editing ? '编辑部门' : '新增部门'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="保存"
        okButtonProps={{ style: { backgroundColor: '#55266c' } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="部门名称" rules={[{ required: true, message: '请输入部门名称' }]}>
            <Input placeholder="如：研发部、销售部" />
          </Form.Item>
          <Form.Item name="code" label="部门编码">
            <Input placeholder="如：RD、SALES（可选）" />
          </Form.Item>
          <Form.Item name="leader" label="负责人">
            <Input placeholder="部门负责人姓名" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue={1}>
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>停用</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ===================== 职位管理 =====================
function PositionTab() {
  const [list, setList] = useState<Position[]>([])
  const [loading, setLoading] = useState(false)
  const [deptOptions, setDeptOptions] = useState<{ id: number; name: string }[]>([])
  const [filterDept, setFilterDept] = useState<number | undefined>()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Position | null>(null)
  const [form] = Form.useForm()

  const fetchList = async (dept_id?: number) => {
    setLoading(true)
    try {
      const res: any = await positionApi.getList(dept_id ? { department_id: dept_id } : {})
      if (res.code === 200) setList(Array.isArray(res.data) ? res.data : res.data?.list || [])
    } catch {
      message.error('获取职位列表失败')
    } finally {
      setLoading(false)
    }
  }

  const fetchDepts = async () => {
    try {
      const res: any = await departmentApi.getOptions()
      if (res.code === 200) setDeptOptions(res.data || [])
    } catch { /* ignore */ }
  }

  useEffect(() => {
    fetchList()
    fetchDepts()
  }, [])

  const handleAdd = () => {
    setEditing(null)
    form.resetFields()
    setModalOpen(true)
  }

  const handleEdit = (record: Position) => {
    setEditing(record)
    form.setFieldsValue({
      ...record,
      department_id: record.department_id || record.department?.id
    })
    setModalOpen(true)
  }

  const handleDelete = async (id: number) => {
    try {
      const res: any = await positionApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        fetchList(filterDept)
      } else {
        message.error(res.message || '删除失败')
      }
    } catch {
      message.error('删除失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      let res: any
      if (editing) {
        res = await positionApi.update(editing.id, values)
      } else {
        res = await positionApi.create(values)
      }
      if (res.code === 200) {
        message.success(editing ? '更新成功' : '创建成功')
        setModalOpen(false)
        fetchList(filterDept)
      } else {
        message.error(res.message || '操作失败')
      }
    } catch {
      // validation error
    }
  }

  const columns = [
    { title: '职位名称', dataIndex: 'name', key: 'name' },
    { title: '编码', dataIndex: 'code', key: 'code', render: (v: string) => v || '-' },
    {
      title: '所属部门',
      key: 'dept',
      render: (_: any, r: Position) => r.department?.name || '-'
    },
    { title: '职级', dataIndex: 'level', key: 'level', width: 80 },
    { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (v: number) => <Tag color={v === 1 ? 'green' : 'default'}>{v === 1 ? '启用' : '停用'}</Tag>
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, r: Position) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该职位？" onConfirm={() => handleDelete(r.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Select
          placeholder="按部门筛选"
          value={filterDept}
          onChange={v => { setFilterDept(v); fetchList(v) }}
          style={{ width: 200 }}
          allowClear
        >
          {deptOptions.map(d => (
            <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>
          ))}
        </Select>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          style={{ backgroundColor: '#55266c' }}
        >新增职位</Button>
      </div>
      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />
      <Modal
        title={editing ? '编辑职位' : '新增职位'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="保存"
        okButtonProps={{ style: { backgroundColor: '#55266c' } }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="职位名称" rules={[{ required: true, message: '请输入职位名称' }]}>
            <Input placeholder="如：前端工程师、配送员" />
          </Form.Item>
          <Form.Item name="code" label="职位编码">
            <Input placeholder="如：FE、DRIVER（可选）" />
          </Form.Item>
          <Form.Item name="department_id" label="所属部门">
            <Select placeholder="请选择部门" allowClear showSearch optionFilterProp="children">
              {deptOptions.map(d => (
                <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="level" label="职级" initialValue={1}>
            <InputNumber min={1} max={10} style={{ width: '100%' }} placeholder="1-10" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue={1}>
            <Select>
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>停用</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

// ===================== 主页面 =====================
function DepartmentPosition() {
  return (
    <div>
      <h2 style={{ marginBottom: 16 }}>部门与职位管理</h2>
      <Tabs
        defaultActiveKey="department"
        items={[
          { key: 'department', label: '部门管理', children: <DepartmentTab /> },
          { key: 'position', label: '职位管理', children: <PositionTab /> }
        ]}
      />
    </div>
  )
}

export default DepartmentPosition
